import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, useMemo } from 'react';
import { FaGripVertical } from 'react-icons/fa';
import { saveLayoutState, loadLayoutState } from '../utils/layoutStorage';
import './ResizableLayout.css';

/** Match RideView / CSS breakpoint for mobile layout */
const MOBILE_LAYOUT_MAX_WIDTH_PX = 768;

function formatFallbackPanelLabel(panelId) {
  if (!panelId || typeof panelId !== 'string') return 'Panel';
  return panelId
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getDesktopLabel(panelId, panelLabels) {
  return panelLabels?.[panelId] ?? formatFallbackPanelLabel(panelId);
}

/** Get stable panel ID from child (data-panel-id, or infer from className, or index fallback) */
function getPanelId(child, index) {
  if (!child?.props) return `panel-${index}`;
  const id = child.props['data-panel-id'];
  if (id) return id;
  const className = typeof child.props.className === 'string' ? child.props.className : '';
  if (className.includes('video-section')) return 'video-panel';
  if (className.includes('graph-section')) return 'telemetry-panel';
  if (className.includes('map-section')) return 'map-panel';
  if (className.includes('stats-section')) return 'stats-panel';
  if (className.includes('suspension-section')) return 'suspension-panel';
  if (className.includes('comments-section')) return 'comments-panel';
  if (className.includes('dropzone-section')) {
    return 'video-panel'; // default; RideView sets data-panel-id on dropzones
  }
  return `panel-${index}`;
}

const ResizableLayout = React.forwardRef(({ children, panelHeaderActions, panelLabels, panelTitleContent }, ref) => {
  const containerRef = useRef(null);
  const layoutRef = useRef(null);
  const [layout, setLayout] = useState({ columns: [], panels: [] });
  const [resizing, setResizing] = useState(null);
  const [panelResizing, setPanelResizing] = useState(null);
  const [resizeLiveHeight, setResizeLiveHeight] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [useMobileStack, setUseMobileStack] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH_PX}px)`).matches
  );
  const isInitialized = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH_PX}px)`);
    const onChange = () => setUseMobileStack(mq.matches);
    mq.addEventListener('change', onChange);
    setUseMobileStack(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  // Save layout via layoutStorage (keeps columns/panels in sync with layout configs)
  const saveLayout = useCallback((layoutToSave) => {
    try {
      const layoutData = {
        columns: layoutToSave.columns.map(col => ({ id: col.id, width: col.width })),
        panels: layoutToSave.panels.map(panel => ({
          id: panel.id,
          columnId: panel.columnId,
          order: panel.order,
          ...(typeof panel.height === 'number' ? { height: panel.height } : {})
        }))
      };
      const current = loadLayoutState() || {};
      saveLayoutState({ ...current, columns: layoutData.columns, panels: layoutData.panels });
      if (process.env.NODE_ENV === 'development') {
        console.log('Layout saved:', layoutData);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save layout:', error);
      }
    }
  }, []);

  // Load layout from layoutStorage; merge by stable panel ID so layout persists when adding/removing video or data
  // When panel count changes (user toggled a panel on/off), use default layout so new panels get their own columns
  const loadSavedLayout = useCallback((childArray, panelIds, prevPanelCount) => {
    try {
      const saved = loadLayoutState();
      if (!saved?.columns?.length || !saved?.panels?.length) return null;

      // Panel set changed (user toggled a panel on/off): use default layout so each panel gets its own column
      if (prevPanelCount > 0 && childArray.length !== prevPanelCount) {
        return null;
      }

      const savedColumns = saved.columns;
      const savedPanels = saved.panels;
      const byId = {};
      const byIndex = {};
      savedPanels.forEach((p, i) => {
        byId[p.id] = p;
        byIndex[i] = p;
      });

      const restoredPanels = childArray.map((child, i) => {
        const pid = panelIds[i];
        const pos = byId[pid] || byIndex[i];
        // New panels (no saved position) get their own column; existing panels keep saved column
        const columnId = pos?.columnId ?? `col-new-${pid}`;
        const order = pos?.order ?? 0;
        const height = typeof pos?.height === 'number' ? pos.height : undefined;
        return { id: pid, content: child, columnId, order, ...(height != null ? { height } : {}) };
      });

      const usedColumnIds = new Set(restoredPanels.map(p => p.columnId));
      let restoredColumns = savedColumns.filter(c => usedColumnIds.has(c.id));
      usedColumnIds.forEach(id => {
        if (!restoredColumns.some(c => c.id === id)) {
          restoredColumns = [...restoredColumns, { id, width: 100 / usedColumnIds.size }];
        }
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Restored layout by panel ID:', { columns: restoredColumns.length, panels: restoredPanels.length });
      }
      return { columns: restoredColumns, panels: restoredPanels };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load layout:', error);
      }
      return null;
    }
  }, []);

  // Initialize layout from children; use stable panel IDs so layout persists when adding/removing video or data
  useEffect(() => {
    const childArray = React.Children.toArray(children).filter(child => child);
    const panelIds = childArray.map((child, i) => getPanelId(child, i));

    setLayout(prev => {
      // If no children, reset
      if (childArray.length === 0) {
        isInitialized.current = false;
        return { columns: [], panels: [] };
      }

      // If already initialized with same panel set (same count and same IDs in order), just update content
      if (
        isInitialized.current &&
        prev.panels.length === childArray.length &&
        prev.columns.length > 0 &&
        prev.panels.every((p, idx) => p.id === panelIds[idx])
      ) {
        return {
          ...prev,
          panels: prev.panels.map((panel, idx) => ({
            ...panel,
            content: childArray[idx]
          }))
        };
      }

      // First time or panel set changed: restore from saved (by panel ID) or use default
      if (!isInitialized.current) {
        isInitialized.current = true;
      }

      const savedLayout = loadSavedLayout(childArray, panelIds, prev.panels.length);
      if (savedLayout) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Restoring saved layout (persistent across add/remove video or data)');
        }
        return savedLayout;
      }

      // Default layout: when adding panels, preserve existing columns and add new ones to the right (user scrolls)
      const numChildren = childArray.length;
      const isAddingPanels = prev.panels.length > 0 && numChildren > prev.panels.length;

      if (isAddingPanels) {
        // Preserve existing columns/panels; add new panels in new columns to the right
        const newPanels = [];
        const existingColumnIds = new Set(prev.columns.map(c => c.id));
        let nextColIdx = prev.columns.length;
        const newColumnWidth = 33; // Fixed width % for new columns so total grows, user scrolls

        childArray.forEach((child, i) => {
          const pid = panelIds[i];
          const existingPanel = prev.panels.find(p => p.id === pid);
          if (existingPanel) {
            newPanels.push({
              ...existingPanel,
              content: child,
              columnId: existingPanel.columnId,
              order: existingPanel.order
            });
          } else {
            const newColId = `col-${nextColIdx}`;
            newPanels.push({
              id: pid,
              content: child,
              columnId: newColId,
              order: 0
            });
            existingColumnIds.add(newColId);
            nextColIdx++;
          }
        });

        const newColumns = prev.columns.map(c => ({ ...c }));
        for (let i = prev.columns.length; i < nextColIdx; i++) {
          newColumns.push({ id: `col-${i}`, width: newColumnWidth });
        }

        return { columns: newColumns, panels: newPanels };
      }

      // First time or removing panels: use default one-column-per-panel
      const columnWidth = 100 / numChildren;
      const newColumns = Array.from({ length: numChildren }, (_, i) => ({
        id: `col-${i}`,
        width: columnWidth
      }));
      const newPanels = childArray.map((child, i) => ({
        id: panelIds[i],
        content: child,
        columnId: `col-${i}`,
        order: 0
      }));
      return { columns: newColumns, panels: newPanels };
    });
  }, [children, loadSavedLayout]);

  // Resize columns: only the resized column changes; others keep width. Total can exceed 100% so viewport scrolls horizontally.
  const MIN_COL_PERCENT = 8;
  const startResize = useCallback((leftColId, rightColId, e) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    const currentLayout = layoutRef.current;
    if (!container || !currentLayout) return;

    const rect = container.getBoundingClientRect();
    const startX = e.clientX - rect.left + container.scrollLeft;
    const contentWidth = Math.max(container.scrollWidth, container.clientWidth);
    if (contentWidth <= 0) return;

    const leftCol = currentLayout.columns.find(c => c.id === leftColId);
    if (!leftCol) return;

    const startLeftWidth = Number(leftCol.width);

    setResizing({ leftColId, rightColId });

    const handleMouseMove = (ev) => {
      const r = container.getBoundingClientRect();
      const currentX = ev.clientX - r.left + container.scrollLeft;
      const deltaPx = currentX - startX;
      const deltaPercent = (deltaPx / contentWidth) * 100;

      const rawNewLeft = startLeftWidth + deltaPercent;
      const newLeft = Math.max(MIN_COL_PERCENT, rawNewLeft);

      setLayout(prev => {
        const newColumns = prev.columns.map(c =>
          c.id === leftColId ? { ...c, width: newLeft } : { ...c }
        );
        return { ...prev, columns: newColumns };
      });

      if (rightColId == null) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const c = containerRef.current;
            if (c) {
              c.scrollLeft = Math.max(0, c.scrollWidth - c.clientWidth);
            }
          });
        });
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      setResizing(null);
      setLayout(prev => {
        saveLayout(prev);
        return prev;
      });
    };

    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseup', handleMouseUp, true);
  }, [saveLayout]);

  // Resize panel height: drag handle at bottom of panel. Persist height; min height enforced.
  const MIN_PANEL_HEIGHT = 120;
  const startPanelResize = useCallback((panelId, e) => {
    e.preventDefault();
    e.stopPropagation();

    const panelEl = e.currentTarget.closest('.column-panel');
    const columnStack = panelEl?.closest('.column-content-stack');
    if (!panelEl) return;

    const startY = e.clientY;
    const startHeight = panelEl.getBoundingClientRect().height;

    setPanelResizing(panelId);
    setResizeLiveHeight(startHeight);

    const handleMouseMove = (ev) => {
      const deltaY = ev.clientY - startY;
      const rawNewHeight = startHeight + deltaY;
      const newHeight = Math.max(MIN_PANEL_HEIGHT, rawNewHeight);

      setResizeLiveHeight(newHeight);
      setLayout(prev => ({
        ...prev,
        panels: prev.panels.map(p =>
          p.id === panelId ? { ...p, height: newHeight } : p
        )
      }));

      if (columnStack) {
        requestAnimationFrame(() => {
          const rect = panelEl.getBoundingClientRect();
          const stackRect = columnStack.getBoundingClientRect();
          if (rect.bottom > stackRect.bottom) {
            columnStack.scrollTop += (rect.bottom - stackRect.bottom);
          }
        });
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setPanelResizing(null);
      setResizeLiveHeight(null);
      setLayout(prev => {
        saveLayout(prev);
        return prev;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [saveLayout]);

  // Drag and drop panels
  const handleDragStart = useCallback((panelId, e) => {
    console.log('Drag started:', panelId);
    e.stopPropagation();
    setDragging({ panelId, dropTarget: null, position: null });
    e.dataTransfer.effectAllowed = 'move';
    // Add some data to make drag work on all browsers
    e.dataTransfer.setData('text/plain', panelId);
    // Make drag image visible
    const dragImage = e.currentTarget.cloneNode(true);
    dragImage.style.opacity = '0.5';
    e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
  }, []);

  const handleDragOver = useCallback((targetPanelId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging || dragging.panelId === targetPanelId) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const position = y < rect.height / 2 ? 'above' : 'below';

    setDragging(prev => ({ ...prev, dropTarget: targetPanelId, position }));
  }, [dragging]);

  const handleDrop = useCallback((targetPanelId, e) => {
    if (e.dataTransfer?.files?.length && !dragging) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (!dragging || !dragging.dropTarget) return;

    setLayout(prev => {
      const newPanels = [...prev.panels];
      const draggedPanel = newPanels.find(p => p.id === dragging.panelId);
      const targetPanel = newPanels.find(p => p.id === targetPanelId);
      
      if (!draggedPanel || !targetPanel) {
        console.log('Panel not found');
        return prev;
      }

      console.log('Moving panel', draggedPanel.id, 'to column', targetPanel.columnId, dragging.position);

      const oldColumnId = draggedPanel.columnId;
      const newColumnId = targetPanel.columnId;

      // Move to target column
      draggedPanel.columnId = newColumnId;
      
      // Get all panels in the target column (excluding the dragged one initially)
      const targetColumnPanels = newPanels
        .filter(p => p.columnId === newColumnId && p.id !== draggedPanel.id)
        .sort((a, b) => a.order - b.order);

      // Find the target index
      const targetIndex = targetColumnPanels.findIndex(p => p.id === targetPanel.id);
      
      // Insert dragged panel at the correct position
      if (dragging.position === 'above') {
        // Insert before target
        targetColumnPanels.splice(targetIndex, 0, draggedPanel);
      } else {
        // Insert after target
        targetColumnPanels.splice(targetIndex + 1, 0, draggedPanel);
      }

      // Renumber all panels in target column
      targetColumnPanels.forEach((panel, idx) => {
        panel.order = idx;
      });

      // If moved from different column, renumber the old column
      if (oldColumnId !== newColumnId) {
        const oldColumnPanels = newPanels
          .filter(p => p.columnId === oldColumnId)
          .sort((a, b) => a.order - b.order);
        
        oldColumnPanels.forEach((panel, idx) => {
          panel.order = idx;
        });
      }

      saveLayout({ ...prev, panels: newPanels });
      return { ...prev, panels: newPanels };
    });

    setDragging(null);
  }, [dragging, saveLayout]);

  const handleDragEnd = useCallback(() => {
    console.log('Drag ended');
    setDragging(null);
  }, []);

  // Handle drop on empty space to create new column
  const handleDropOnNewColumn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Drop on new column');
    
    if (!dragging || !dragging.panelId) {
      return;
    }

    setLayout(prev => {
      const newPanels = [...prev.panels];
      const draggedPanel = newPanels.find(p => p.id === dragging.panelId);
      
      if (!draggedPanel) {
        return prev;
      }

      const oldColumnId = draggedPanel.columnId;
      
      // Create new column
      const newColumnId = `col-${Date.now()}`;
      const newColumn = { id: newColumnId, width: 25 };
      
      // Move panel to new column
      draggedPanel.columnId = newColumnId;
      draggedPanel.order = 0;
      
      // Renumber panels in old column
      const oldColumnPanels = newPanels
        .filter(p => p.columnId === oldColumnId)
        .sort((a, b) => a.order - b.order);
      
      oldColumnPanels.forEach((panel, idx) => {
        panel.order = idx;
      });

      const newLayout = {
        columns: [...prev.columns, newColumn],
        panels: newPanels
      };
      saveLayout(newLayout);
      return newLayout;
    });

    setDragging(null);
  }, [dragging, saveLayout]);

  const handleDragOverNewColumn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragging) {
      setDragging(prev => ({ ...prev, dropTarget: 'new-column', position: 'new' }));
    }
  }, [dragging]);

  // Reset layout function
  const resetLayout = useCallback(() => {
    const childArray = React.Children.toArray(children).filter(child => child);
    const numChildren = childArray.length;
    const numColumns = numChildren;
    const columnWidth = 100 / numColumns;

    const newLayout = {
      columns: Array.from({ length: numColumns }, (_, i) => ({
        id: `col-${i}`,
        width: columnWidth
      })),
      panels: childArray.map((child, i) => ({
        id: `panel-${i}`,
        content: child,
        columnId: `col-${i}`,
        order: 0
      }))
    };

    setLayout(newLayout);
    saveLayout(newLayout);
  }, [children, saveLayout]);

  // Expose reset method to parent
  useImperativeHandle(ref, () => ({
    resetLayout
  }), [resetLayout]);

  // Group panels by column and filter out empty columns. Use raw column widths (no normalization) so viewport can expand.
  const columnData = layout.columns
    .map(column => {
      const panelsInColumn = layout.panels
        .filter(p => p.columnId === column.id)
        .sort((a, b) => a.order - b.order);
      
      return {
        column,
        panels: panelsInColumn
      };
    })
    .filter(cd => cd.panels.length > 0);

  const columnDisplayData = columnData;
  const totalPanels = columnDisplayData.reduce((sum, cd) => sum + cd.panels.length, 0);

  const flatPanels = useMemo(() => {
    return layout.columns
      .map((column) =>
        layout.panels
          .filter((p) => p.columnId === column.id)
          .sort((a, b) => a.order - b.order)
      )
      .filter((panelsInColumn) => panelsInColumn.length > 0)
      .flat();
  }, [layout]);

  const renderPanelChrome = (panel, { mobileSingleColumn } = {}) => {
    const hasFixedHeight = panel.height != null || (panelResizing === panel.id && resizeLiveHeight != null);
    const effectiveHeight = panelResizing === panel.id && resizeLiveHeight != null ? resizeLiveHeight : panel.height;
    const hasActions = panelHeaderActions && panelHeaderActions[panel.id];
    const desktopLabel = getDesktopLabel(panel.id, panelLabels);
    const titleOverride = panelTitleContent && panelTitleContent[panel.id];
    const showDragHandle = totalPanels > 1 || columnDisplayData.length > 1 || hasActions || titleOverride;

    return (
      <div
        key={panel.id}
        data-panel-instance-id={panel.id}
        className={`column-panel ${mobileSingleColumn ? 'column-panel--mobile-stack' : ''} ${dragging?.panelId === panel.id ? 'dragging' : ''} ${dragging?.dropTarget === panel.id ? `drag-over-${dragging.position}` : ''} ${hasFixedHeight ? 'panel-has-fixed-height' : ''}`}
        style={hasFixedHeight ? { height: effectiveHeight, flex: '0 0 auto' } : mobileSingleColumn ? { flex: '0 0 auto', minHeight: 220 } : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          handleDragOver(panel.id, e);
        }}
        onDrop={(e) => handleDrop(panel.id, e)}
      >
        {showDragHandle && (
          <div
            className="panel-header"
            draggable="true"
            onDragStart={(e) => handleDragStart(panel.id, e)}
            onDragEnd={handleDragEnd}
            style={{ cursor: dragging?.panelId === panel.id ? 'grabbing' : 'grab' }}
          >
            <div className="drag-handle" title="Drag to move">
              <FaGripVertical />
            </div>
            <div className="panel-header-title">
              {titleOverride ?? desktopLabel}
            </div>
            {hasActions ? <div className="panel-header-actions">{panelHeaderActions[panel.id]}</div> : null}
          </div>
        )}
        <div className={`panel-content ${hasFixedHeight ? 'panel-content-scrollable' : ''}`}>{panel.content}</div>
        {!mobileSingleColumn ? (
          <div
            className={`panel-resize-handle ${panelResizing === panel.id ? 'active' : ''}`}
            onMouseDown={(e) => startPanelResize(panel.id, e)}
            title="Drag to resize panel height"
          >
            <div className="panel-resize-handle-line" />
          </div>
        ) : null}
      </div>
    );
  };

  if (layout.columns.length === 0) return null;

  if (useMobileStack && flatPanels.length > 0) {
    return (
      <div className="resizable-layout-container resizable-layout-container--mobile-stack">
        <div ref={containerRef} className="resizable-columns resizable-columns--mobile-stack">
          <div className="resizable-column resizable-column--mobile-stack-col">
            <div className="column-content-stack column-content-stack--mobile-stack">
              {flatPanels.map((panel) => renderPanelChrome(panel, { mobileSingleColumn: true }))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="resizable-layout-container">
      <div 
        ref={containerRef}
        className={`resizable-columns ${resizing ? 'resizing' : ''} ${panelResizing ? 'panel-resizing' : ''}`}
      >
        {columnDisplayData.map((cd, colIdx) => (
          <React.Fragment key={cd.column.id}>
            <div 
              className="resizable-column"
              style={{ width: `${cd.column.width}%`, flexShrink: 0 }}
            >
              <div className="column-content-stack">
                {cd.panels.map((panel) => renderPanelChrome(panel))}
              </div>
            </div>

            {colIdx < columnDisplayData.length - 1 ? (
              <div
                className={`resize-handle ${resizing?.leftColId === cd.column.id ? 'active' : ''}`}
                onMouseDown={(e) => startResize(cd.column.id, columnDisplayData[colIdx + 1].column.id, e)}
                title="Drag to resize columns"
              >
                <div className="resize-handle-line" />
              </div>
            ) : (
              <div
                className={`resize-handle resize-handle-last ${resizing?.leftColId === cd.column.id ? 'active' : ''}`}
                onMouseDown={(e) => startResize(cd.column.id, null, e)}
                title="Drag to resize column"
              >
                <div className="resize-handle-line" />
              </div>
            )}
          </React.Fragment>
        ))}

        {dragging && (
          <div
            className={`new-column-drop-zone ${dragging?.dropTarget === 'new-column' ? 'drag-over-new' : ''}`}
            onDragOver={handleDragOverNewColumn}
            onDrop={handleDropOnNewColumn}
          >
            <div className="new-column-indicator">
              Drop here to create new column
            </div>
          </div>
        )}
      </div>

      {/* Edge drop zone: fixed at right when dragging, no scroll needed */}
      {dragging && (
        <div
          className={`new-column-edge-drop-zone ${dragging?.dropTarget === 'new-column' ? 'drag-over-new' : ''}`}
          onDragOver={handleDragOverNewColumn}
          onDrop={handleDropOnNewColumn}
          title="Drop to create new column"
        >
          <div className="new-column-edge-indicator">
            + New column
          </div>
        </div>
      )}
    </div>
  );
});

ResizableLayout.displayName = 'ResizableLayout';

export default ResizableLayout;
